/**
 * Team — who can see the money, and what they can do with it.
 *
 * Super admin only. Account creation lives here rather than in a shell script so that granting sight of
 * every tenant's money is role-gated and permanently recorded: the audit trail names who granted what to
 * whom. The bootstrap script creates the first super admin and then refuses to run again.
 *
 * The role descriptions are written for the person doing the granting, in terms of what the account will be
 * able to DO — not as three words that mean nothing until you have already made the mistake.
 */
import { useState } from "react";
import { api } from "../lib/api";
import { useLoader, useSession } from "../lib/session";
import { dateLong, relative } from "../lib/format";
import { ActionButton, C, card, CardHead, Drawer, EmptyState, ErrorState, input, label, labelText, Skeleton } from "../ui/primitives";

const ROLES = [
  { key: "viewer", name: "Viewer", can: "Read everything — wallets, movements, reconciliation, queues. Changes nothing." },
  { key: "manager", name: "Manager", can: "Everything a viewer can, plus resolve breaks, replay queued events and change fee rates." },
  { key: "super_admin", name: "Super admin", can: "Everything a manager can, plus grant and revoke console access. Give this sparingly." },
] as const;

export function Team() {
  const { user } = useSession();
  const { data, error, loading, reload } = useLoader(() => api.consoleUsers(), []);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ id: string; text: string; ok: boolean } | null>(null);

  async function toggle(id: string, isActive: boolean) {
    setBusy(id); setNotice(null);
    try { await api.setConsoleUserActive(id, isActive); reload(); }
    catch (e) { setNotice({ id, ok: false, text: e instanceof Error ? e.message : "Could not change that account" }); }
    finally { setBusy(null); }
  }

  if (loading) return <div style={card}><Skeleton rows={3} label="Loading the team…" />
  </div>;
  if (error) return <div style={card}><ErrorState title="Couldn't load the team." body={error} onRetry={reload} /></div>;

  const users = data?.users ?? [];

  return (
    <>
      <div style={{ ...card, padding: "15px 17px", display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ fontWeight: 800, fontSize: 15 }}>What each role can do</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 10 }}>
          {ROLES.map((r) => (
            <div key={r.key} style={{ padding: "11px 13px", background: "var(--subtle)", border: "1px solid var(--line)", borderRadius: 11 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800 }}>{r.name}</div>
              <div style={{ fontSize: 11.5, color: "var(--ink2)", lineHeight: 1.5, marginTop: 2 }}>{r.can}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={card}>
        <CardHead title="Console accounts"
          sub="Everyone who can sign in. Granting access is recorded permanently — who granted what, to whom, and when."
          right={<ActionButton small onClick={() => setOpen(true)}>Grant access</ActionButton>} />

        {users.length === 0 ? (
          <EmptyState title="No accounts yet." body="That should be impossible — you are signed in." />
        ) : users.map((u) => (
          <div key={u.id} style={{ display: "grid", gridTemplateColumns: "1fr 120px 150px 140px", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--line)", alignItems: "center" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800 }}>
                {u.name}{u.id === user?.id && <span style={{ color: "var(--muted)", fontWeight: 600 }}> · you</span>}
              </div>
              <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>{u.email}</div>
            </div>

            <span style={{ fontSize: 11.5, fontWeight: 800 }}>
              {ROLES.find((r) => r.key === u.role)?.name ?? u.role}
            </span>

            <div style={{ fontSize: 11, fontWeight: 700 }}>
              {!u.isActive
                ? <span style={{ color: C.danger }}>Deactivated</span>
                : u.enrolled
                  ? <span style={{ color: C.good }}>Active</span>
                  // Granted but never enrolled: the account looks live and cannot actually be signed into.
                  // Worth naming, or you wonder why a colleague says they have no access.
                  : <span style={{ color: C.warn }}>Awaiting authenticator setup</span>}
              <div style={{ fontSize: 10.5, color: "var(--muted)", fontWeight: 600 }}>added {dateLong(u.createdAt)} · {relative(u.createdAt)}</div>
            </div>

            <div style={{ textAlign: "right" }}>
              {u.id === user?.id ? (
                <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600 }}>—</span>
              ) : (
                <ActionButton small tone="quiet" onClick={() => void toggle(u.id, !u.isActive)}>
                  {busy === u.id ? "…" : u.isActive ? "Deactivate" : "Reactivate"}
                </ActionButton>
              )}
              {notice?.id === u.id && (
                <div style={{ fontSize: 11, fontWeight: 700, color: C.danger, marginTop: 4, lineHeight: 1.4 }}>{notice.text}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {open && <GrantDrawer onClose={() => setOpen(false)} onDone={() => { setOpen(false); reload(); }} />}
    </>
  );
}

function GrantDrawer({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<"viewer" | "manager" | "super_admin">("viewer");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (password.length < 12) { setErr("The starting password must be at least 12 characters."); return; }
    setBusy(true); setErr(null);
    try { await api.grantConsoleUser({ email: email.trim(), name: name.trim(), password, role }); onDone(); }
    catch (e) { setErr(e instanceof Error ? e.message : "Could not grant access"); setBusy(false); }
  }

  return (
    <Drawer title="Grant console access" onClose={onClose}>
      <label style={label}><span style={labelText}>Work email</span>
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="colleague@bluepilo.com" style={input} /></label>
      <label style={label}><span style={labelText}>Full name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ada Okafor" style={input} /></label>

      <label style={label}><span style={labelText}>Role</span>
        <select value={role} onChange={(e) => setRole(e.target.value as typeof role)} style={input}>
          {ROLES.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
        </select>
      </label>
      <div style={{ fontSize: 11.5, color: "var(--ink2)", lineHeight: 1.5, marginTop: -6 }}>
        {ROLES.find((r) => r.key === role)!.can}
      </div>

      <label style={label}><span style={labelText}>Starting password — 12 characters or more</span>
        <input value={password} onChange={(e) => setPassword(e.target.value)} type="text" placeholder="give this to them directly" style={input} /></label>

      <div style={{ padding: "11px 13px", border: `1px solid ${C.warnLine}`, background: C.warnSoft, borderRadius: 11, fontSize: 12, lineHeight: 1.55, color: "var(--ink2)" }}>
        <b>Hand this password to them yourself</b> — it is shown here once and is not recoverable afterwards.
        They will set up an authenticator on their first sign-in, and <b>no session is issued until they
        confirm a live code</b>, so the password alone is not access.
        <br /><br />
        This grant is recorded permanently against your name.
      </div>

      {err && <div style={{ fontSize: 12, fontWeight: 700, color: C.danger }}>{err}</div>}
      <div style={{ display: "flex", gap: 9 }}>
        <ActionButton tone="quiet" onClick={onClose}>Cancel</ActionButton>
        <div style={{ flex: 1 }}>
          <button type="button" onClick={() => void save()} disabled={busy}
            style={{ width: "100%", padding: 12, border: "none", borderRadius: 11, background: busy ? "var(--muted)" : C.accent, color: "#fff", fontSize: 12.5, fontWeight: 800, cursor: busy ? "wait" : "pointer" }}>
            {busy ? "Granting…" : "Grant access — recorded under my name"}
          </button>
        </div>
      </div>
    </Drawer>
  );
}
